import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { getLocale, useIntl } from '@edx/frontend-platform/i18n';

import { cohortsApi } from '../../api';
import { useHeaderFooter, useFacilitatorStatus } from '../../hooks';
import type { Cohort } from '../../types';
import {
  ALLOWED_SITE_LANGUAGES, DEFAULT_LANGUAGE, copyTextToClipboard, resolveApiErrorMessage,
} from '../../utils';

import messages from './messages';

import '../cohortSubpage.scss';
import './index.scss';

interface CohortCreateProps {
  children?: React.ReactNode;
}

/** Resolves the active portal locale to a cohort `language`, falling back to English. */
const getCohortLanguage = (): string => (
  ALLOWED_SITE_LANGUAGES.includes(getLocale()) ? getLocale() : DEFAULT_LANGUAGE
);

const buildInviteLink = (slug: string): string => (
  `${window.location.origin}/groups/join/${slug}`
);

/**
 * Create-a-cohort flow: a form (name + description) that, on success, swaps in
 * an invite-link success screen — two states in one view.
 */
const CohortCreate: React.FC<CohortCreateProps> = ({ children }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { headerContent, footerContent } = useHeaderFooter(children);
  const { loaded, canCreateCohorts } = useFacilitatorStatus();

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [createdCohort, setCreatedCohort] = useState<Cohort | null>(null);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const handleCancel = (): void => navigate('/cohorts/mine');
  const handleGoToMyCohorts = (): void => navigate('/cohorts/mine');

  const handleSubmit = async (): Promise<void> => {
    if (!name.trim()) {
      setNameError(intl.formatMessage(messages.requiredNameError));
      return;
    }

    setNameError(null);
    setSubmitError(null);
    setIsSubmitting(true);

    await cohortsApi.createCohort(
      { name: name.trim(), description: description.trim(), language: getCohortLanguage() },
      (cohort) => {
        setCreatedCohort(cohort);
        setIsSubmitting(false);
      },
      (error) => {
        // No machine-key mapping: `POST /cohorts/` has no cohort-specific error
        // code, so this falls back to `payload.detail`, then the generic message.
        setSubmitError(resolveApiErrorMessage(
          error,
          {},
          intl.formatMessage(messages.submitError),
        ));
        setIsSubmitting(false);
      },
    );
  };

  const handleCopyLink = async (): Promise<void> => {
    if (!createdCohort) {
      return;
    }
    // Legacy fallback inside: `navigator.clipboard` does not exist over plain
    // HTTP, so the direct call threw and the button never confirmed.
    const copied = await copyTextToClipboard(buildInviteLink(createdCohort.slug));
    setIsCopied(copied);
  };

  const renderForm = () => (
    // A plain <div>, not a <form> — mirrors Contact/index.tsx: avoids native HTML5
    // constraint validation (from the `required` attribute) intercepting the
    // submit before our own validation/error state ever runs.
    <div className="cohort-create-form">
      <div className="form-row">
        <label htmlFor="cohort-name">{intl.formatMessage(messages.nameLabel)}</label>
        <input
          id="cohort-name"
          name="name"
          type="text"
          placeholder={intl.formatMessage(messages.namePlaceholder)}
          value={name}
          onChange={(e) => { setName(e.target.value); setNameError(null); }}
          required
        />
        {nameError && <span className="form-error form-error-custom">{nameError}</span>}
      </div>
      <div className="form-row">
        <label htmlFor="cohort-description">{intl.formatMessage(messages.descriptionLabel)}</label>
        <textarea
          id="cohort-description"
          name="description"
          rows={5}
          placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      {submitError && <p className="form-error form-error-custom">{submitError}</p>}
      <div className="cohort-create-actions">
        <button
          type="button"
          className="leg-primary-button"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {intl.formatMessage(isSubmitting ? messages.creatingButton : messages.createButton)}
        </button>
        <button
          type="button"
          className="cohort-create-cancel"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          {intl.formatMessage(messages.cancelButton)}
        </button>
      </div>
    </div>
  );

  const renderSuccess = (cohort: Cohort) => {
    const inviteLink = buildInviteLink(cohort.slug);

    return (
      <div className="cohort-create-success">
        <div className="cohort-create-success-banner">
          <span className="cohort-create-success-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
              <path d="M5 12.5l4.5 4.5L19 7.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <div>
            <div className="cohort-create-success-title">{intl.formatMessage(messages.successBanner)}</div>
            <p className="cohort-create-success-subtitle">
              {intl.formatMessage(messages.successBannerSubtitle)}
            </p>
          </div>
        </div>

        <div className="cohort-create-card">
          <div className="cohort-create-card-title">{intl.formatMessage(messages.inviteLinkLabel)}</div>
          <p className="cohort-create-card-help">{intl.formatMessage(messages.inviteLinkHelp)}</p>
          <div className="cohort-invite-link-box">
            <label className="sr-only" htmlFor="cohort-invite-link">
              {intl.formatMessage(messages.inviteLinkLabel)}
            </label>
            <input id="cohort-invite-link" type="text" value={inviteLink} readOnly />
          </div>
          <button type="button" className="cohort-create-copy-button" onClick={handleCopyLink}>
            {intl.formatMessage(isCopied ? messages.copiedLinkButton : messages.copyLinkButton)}
          </button>
        </div>

        <div className="cohort-create-what-happens">
          <div className="cohort-create-card-title">{intl.formatMessage(messages.whatHappensTitle)}</div>
          <ol className="cohort-create-steps">
            <li>{intl.formatMessage(messages.whatHappensStep1)}</li>
            <li>{intl.formatMessage(messages.whatHappensStep2)}</li>
            <li>{intl.formatMessage(messages.whatHappensStep3)}</li>
          </ol>
        </div>

        <div className="cohort-create-actions">
          <button type="button" className="cohort-create-primary-button" onClick={handleGoToMyCohorts}>
            {intl.formatMessage(messages.goToMyCohortsButton)}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <input type="hidden" id="anPageName" name="page" defaultValue="cohort-create" />
      <div className="container-center-horizontal">
        <div className="cohort-create screen cohort-subpage">
          <div className="homepage">
            {headerContent}
          </div>
          <div className="legatum-container">
            <div className="c-section">
              <nav className="cohort-create-breadcrumb" aria-label={intl.formatMessage(messages.breadcrumbAriaLabel)}>
                <NavLink className="cohort-create-breadcrumb-link" to="/cohorts">
                  {intl.formatMessage(messages.breadcrumbCohorts)}
                </NavLink>
                {createdCohort ? (
                  <>
                    <span> › {intl.formatMessage(messages.breadcrumbCreate)}</span>
                    <span className="cohort-create-breadcrumb-current">
                      {' › '}
                      {intl.formatMessage(messages.breadcrumbCreated)}
                    </span>
                  </>
                ) : (
                  <span> › {intl.formatMessage(messages.breadcrumbCreate)}</span>
                )}
              </nav>
              {/* The success screen is its own card stack — the page heading would
                  duplicate the banner, so it only renders while the form is up. */}
              {!createdCohort && (
                <h1 className="leg-title">{intl.formatMessage(messages.pageTitle)}</h1>
              )}
              {loaded && canCreateCohorts && !createdCohort && (
                <p className="cohort-create-subtitle">{intl.formatMessage(messages.pageSubtitle)}</p>
              )}
              {loaded && !canCreateCohorts && (
                <>
                  <p className="leg-p">{intl.formatMessage(messages.restrictedMessage)}</p>
                  <div className="items-center space-bottom">
                    <div className="leg-primary-button">
                      <NavLink to="/cohorts">{intl.formatMessage(messages.backToCohortsLink)}</NavLink>
                    </div>
                  </div>
                </>
              )}
              {loaded && canCreateCohorts && (
                createdCohort ? renderSuccess(createdCohort) : renderForm()
              )}
            </div>
          </div>
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default CohortCreate;
